/**
 * Lease Office view-model (spec §7.3, §9.2; Hub only): principal, rate, next due in game time, payment, arrears
 * and fees, collections, the pay-arrears and pay-extra figures, insurance after ownership, insurer debt, and the
 * newest history entries. M7 S33 seeds (spec §12.4): the statement's issuer and Voss's signature (PC-8), the Lessee
 * Conduct Covenant with Duarte's reading at signing, the clerk on duty (C8), and the repo crew while a repo is on.
 */
import { arrearsDue } from '../../career/lease'
import { EPSILON } from '../../career/ops'
import type { Career, LeaseEventKind } from '../../career/types'
import type { M2Data } from '../../data/m2Schemas'
import { UI_CONFIG, type UiConfig } from '../config'
import { formatCr, formatDueIn, formatDuration } from '../format'
import { signatureLine, type SeedLine } from './seeds'
import { collectionsView, type CollectionsView } from './station'

export interface LeaseHistoryRow {
  when: string
  label: string
  amount: string
  kind: LeaseEventKind
}

export interface LeaseModel {
  owned: boolean
  principal: string
  rate: string
  payment: string
  nextDue: string
  missed: number
  arrears: string
  fees: string
  collections: CollectionsView
  /** What paying the arrears costs now (0 when there is nothing to pay). */
  arrearsDue: number
  arrearsLabel: string
  canPayArrears: boolean
  /** Most that "pay extra" can pay now: min(wallet, principal), floored to whole credits. */
  maxExtra: number
  insured: boolean
  premium: string
  debt: string | null
  history: LeaseHistoryRow[]
  /** "Tethys Crew Finance · lease statement · Signed: Maren Voss, …" (null without the seed). */
  statement: string | null
  /** The Covenant as signed (null without the seed). */
  covenant: CovenantView | null
  /** "Tethys Asset Recovery has the recovery order (TETHYS ASSET RECOVERY livery)" while a repo is ordered, else null. */
  recovery: string | null
}

export interface CovenantView {
  title: string
  clauses: string[]
  /** Who read it aloud at signing, and the words. */
  reading: SeedLine
}

const KIND_LABELS: Readonly<Record<LeaseEventKind, string>> = {
  paid: 'Payment',
  missed: 'Missed payment',
  extra: 'Extra payment',
  arrearsPaid: 'Arrears paid',
  lateFee: 'Late fee',
  interest: 'Interest',
  rebuildFee: 'Rebuild fee',
  repoFee: 'Repo fee',
  premium: 'Insurance premium',
  debtIncurred: 'Insurer debt',
  debtPaid: 'Debt repaid',
  ownership: 'Ownership',
}

export function leaseEventLabel(kind: LeaseEventKind): string {
  return KIND_LABELS[kind]
}

export function leaseModel(data: M2Data, career: Career, gameTime: number, cfg: UiConfig = UI_CONFIG): LeaseModel {
  const l = career.lease
  const office = data.lease.office
  const due = l.owned ? 0 : arrearsDue(career)
  const owes = !l.owned && (due > EPSILON || l.missed > 0)
  const history = l.history
    .slice(-cfg.historyRows)
    .reverse()
    .map((e) => ({
      when: `T+${formatDuration(e.t)}`,
      label: leaseEventLabel(e.kind),
      amount: e.kind === 'ownership' ? '' : formatCr(e.amount),
      kind: e.kind,
    }))
  return {
    owned: l.owned,
    principal: formatCr(l.principal),
    rate: `${(l.ratePerCycle * 100).toFixed(1)}% per cycle`,
    payment: formatCr(Math.min(l.payment, l.principal)),
    nextDue: l.owned ? 'Paid off' : formatDueIn(l.nextDueAt, gameTime),
    missed: l.missed,
    arrears: formatCr(l.arrears),
    fees: formatCr(l.feesDue),
    collections: collectionsView(l.collections, l.repoPausedUntil > gameTime),
    arrearsDue: due,
    arrearsLabel: owes ? `Pay arrears: ${formatCr(due)}` : 'No arrears',
    canPayArrears: owes && career.wallet + EPSILON >= due,
    maxExtra: l.owned ? 0 : Math.max(0, Math.floor(Math.min(career.wallet, l.principal) + EPSILON)),
    insured: l.insured,
    premium: formatCr(data.lease.insurance.premiumPerCycle),
    debt: career.debt > 0 ? formatCr(career.debt) : null,
    history,
    statement: office === undefined ? null : `${office.statement.issuer} · ${signatureLine(office.statement.signedBy)}`,
    covenant: office === undefined ? null : { title: office.covenant.title, clauses: [...office.covenant.clauses], reading: { ...office.covenant.reading } },
    recovery:
      office !== undefined && !l.owned && l.collections === 'repo'
        ? `${office.recovery.crew} has the recovery order (${office.recovery.liveryTag} livery).`
        : null,
  }
}
